import { useState, type FormEvent } from 'react';
import { PASSWORD_MIN_LENGTH } from '@repo/auth-contracts';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { PasswordField } from '@/components/password-field';
import type { Copy } from '@/lib/messages';

interface OfferPasswordStepProps {
  copy: Copy;
  pending: boolean;
  /** Form-level error (network, throttle, …) — rendered as the top alert. */
  formError: string | null;
  /** Field-level error (an unusable password) — rendered on the field. */
  fieldError: string | null;
  onSet: (password: string) => void;
  onSkip: () => void;
}

/**
 * Offered once to accounts that have never been asked — a fallback for the day
 * email is unreachable. Skipping is a first-class outcome: the login is
 * already complete either way (docs/auth/40-features/login-password.md).
 */
export function OfferPasswordStep({
  copy,
  pending,
  formError,
  fieldError,
  onSet,
  onSkip,
}: OfferPasswordStepProps) {
  const [password, setPassword] = useState('');
  const [acting, setActing] = useState<'set' | 'skip' | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (password.length < PASSWORD_MIN_LENGTH) return;
    setActing('set');
    onSet(password);
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {formError ? <Alert tone="error">{formError}</Alert> : null}
      <PasswordField
        id="new-password"
        label={copy.passwordNewLabel}
        error={fieldError}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
        showLabel={copy.passwordShow}
        hideLabel={copy.passwordHide}
        hint={copy.passwordHint}
        autoFocus
      />
      {/* Gated on the real policy: an enabled button whose submit the server
          must refuse is a promise the screen cannot keep. */}
      <Button
        type="submit"
        loading={pending && acting === 'set'}
        disabled={pending || password.length < PASSWORD_MIN_LENGTH}
      >
        {copy.passwordSetSubmit}
      </Button>
      <Button
        type="button"
        variant="link"
        loading={pending && acting === 'skip'}
        onClick={() => {
          setActing('skip');
          onSkip();
        }}
        disabled={pending}
      >
        {copy.passwordSkip}
      </Button>
    </form>
  );
}
